/**
 * Utility functions for haptic and audio feedback
 */

/**
 * Creates a beep sound using Web Audio API
 * @param frequency - Frequency in Hz
 * @param duration - Duration in milliseconds
 * @param volume - Volume between 0 and 1
 */
function createBeep(frequency: number, duration: number, volume: number = 0.3): void {
  try {
    // Check if Web Audio API is supported
    if (!window.AudioContext && !(window as any).webkitAudioContext) {
      return;
    }

    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    
    // Create oscillator for tone generation
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    // Connect audio nodes
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    // Configure the beep
    oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime);
    oscillator.type = 'sine';
    
    // Set volume envelope to avoid clicks
    gainNode.gain.setValueAtTime(0, audioContext.currentTime);
    gainNode.gain.linearRampToValueAtTime(volume, audioContext.currentTime + 0.01);
    gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + duration / 1000);
    
    // Play the beep
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + duration / 1000);
  } catch (error) {
    // Fail silently if audio context creation fails
    console.debug('Audio feedback not available:', error);
  }
}

/**
 * Provides haptic and audio feedback for successful scan
 * Falls back gracefully if vibration or audio is not supported
 */
export function provideScanFeedback(): void {
  // Vibration feedback
  if ('vibrate' in navigator) {
    navigator.vibrate(100);
  }
  
  // Audio feedback - short, mid-pitched beep
  createBeep(800, 150);
}

/**
 * Provides haptic and audio feedback for successful purchase/consume action
 */
export function provideSuccessFeedback(): void {
  // Vibration feedback
  if ('vibrate' in navigator) {
    navigator.vibrate([100, 50, 100]);
  }
  
  // Audio feedback - pleasant success tone (two-tone ascending)
  createBeep(600, 150);
  setTimeout(() => createBeep(800, 150), 100);
}

/**
 * Provides haptic and audio feedback for errors
 */
export function provideErrorFeedback(): void {
  // Vibration feedback
  if ('vibrate' in navigator) {
    navigator.vibrate(200);
  }
  
  // Audio feedback - lower pitched, longer error tone
  createBeep(300, 300);
} 